import type {
  ApiBlogCategory,
  ApiBlogCluster,
  ApiBlogGoal,
  ApiBlogPost,
  ApiBlogPostSummary,
  ApiBlogSeo,
  ApiPaginatedPosts,
  ApiUpsertClusterBody,
  ApiUpsertPostBody,
} from "./blogApiShapes";
import type {
  BlogCategory,
  BlogCluster,
  BlogPost,
  BlogPostGoal,
  BlogPostSummary,
  BlogSeo,
  PaginatedPosts,
  UpsertClusterInput,
  UpsertPostInput,
} from "./blogTypes";

// ── Mappers snake_case → camelCase (puros) ──────────────────────────────────

const mapSeo = (s: ApiBlogSeo): BlogSeo => ({
  metaTitle: s.meta_title ?? null,
  metaDescription: s.meta_description ?? null,
  ogImageUrl: s.og_image_url ?? null,
  canonicalUrl: s.canonical_url ?? null,
  noindex: s.noindex ?? false,
});

export const mapSummary = (p: ApiBlogPostSummary): BlogPostSummary => ({
  id: p.id,
  slug: p.slug,
  title: p.title,
  excerpt: p.excerpt ?? "",
  category: p.category ?? null,
  tags: p.tags ?? [],
  authorName: p.author_name ?? null,
  authorId: p.author_id ?? null,
  clusterId: p.cluster_id ?? null,
  role: p.role ?? null,
  status: p.status,
  publishedAt: p.published_at ?? null,
  readingTimeMin: p.reading_time_min ?? 0,
});

/**
 * Meta ainda não existe no notifyme (E6.1 é novo) — chega opcional/`null` e
 * vira `null` no domínio, igual ao padrão de `cluster_id`/`role`/`faq`. Não
 * há "valor neutro" sensato para uma meta parcial: ou o post tem meta
 * completa, ou não tem meta nenhuma.
 */
const mapGoal = (g: ApiBlogGoal): BlogPostGoal => ({
  primaryKeyword: g.primary_keyword,
  targetPosition: g.target_position,
  targetImpressions30d: g.target_impressions_30d,
  targetClicks30d: g.target_clicks_30d,
  targetLeads90d: g.target_leads_90d,
  deadline: g.deadline,
});

export const mapPost = (p: ApiBlogPost): BlogPost => ({
  ...mapSummary(p),
  contentHtml: p.content_html ?? "",
  faq: p.faq ?? [],
  scheduledFor: p.scheduled_for ?? null,
  createdAt: p.created_at,
  updatedAt: p.updated_at,
  seo: mapSeo(p.seo),
  goal: p.goal ? mapGoal(p.goal) : null,
});

export const mapCluster = (c: ApiBlogCluster): BlogCluster => ({
  id: c.id,
  slug: c.slug,
  name: c.name,
  headKeyword: c.head_keyword,
  description: c.description ?? "",
  pillarPostId: c.pillar_post_id ?? null,
  targetLandingPath: c.target_landing_path,
  status: c.status,
  postCount: c.post_count ?? 0,
});

export const mapCategory = (c: ApiBlogCategory): BlogCategory => ({
  slug: c.slug,
  name: c.name,
  postCount: c.post_count ?? 0,
});

export const mapPaginated = (page: ApiPaginatedPosts): PaginatedPosts => ({
  items: (page.items ?? []).map(mapSummary),
  page: page.page,
  pageSize: page.page_size,
  totalItems: page.total_items,
  totalPages: page.total_pages,
});

// ── Mapper de saída (domínio → corpo da API) ────────────────────────────────

const toGoalBody = (g: BlogPostGoal): ApiBlogGoal => ({
  primary_keyword: g.primaryKeyword,
  target_position: g.targetPosition,
  target_impressions_30d: g.targetImpressions30d,
  target_clicks_30d: g.targetClicks30d,
  target_leads_90d: g.targetLeads90d,
  deadline: g.deadline,
});

export const toUpsertBody = (input: UpsertPostInput): ApiUpsertPostBody => ({
  title: input.title,
  slug: input.slug,
  excerpt: input.excerpt,
  content_html: input.contentHtml,
  category: input.category,
  tags: input.tags,
  author_name: input.authorName,
  author_id: input.authorId,
  cluster_id: input.clusterId,
  role: input.role,
  faq: input.faq,
  meta_title: input.seo.metaTitle,
  meta_description: input.seo.metaDescription,
  og_image_url: input.seo.ogImageUrl,
  canonical_url: input.seo.canonicalUrl,
  noindex: input.seo.noindex,
  goal: input.goal ? toGoalBody(input.goal) : null,
});

export const toUpsertClusterBody = (input: UpsertClusterInput): ApiUpsertClusterBody => ({
  slug: input.slug,
  name: input.name,
  head_keyword: input.headKeyword,
  description: input.description,
  pillar_post_id: input.pillarPostId,
  target_landing_path: input.targetLandingPath,
  status: input.status,
});
